import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { activeTrail, isNavGroup, leafPaths, navItemKey } from "../../config/navConfig";

/**
 * Верхняя строка разделов группы — как в Битрикс24 (правка №52, идея Светы 03.11.2026 17:10). [Claude]
 * Показывается в режиме меню «сверху», когда открыта страница из группы левого меню.
 * Вложенная группа («Тестирование», «Оформление») — выпадающий пункт; что не влезло — в «Ещё ▾».
 * Порядок и видимость — личные, из «Настройки меню».
 */

const tabClass = (active) => `whitespace-nowrap px-3 py-1.5 rounded-lg text-sm transition-colors ${
    active ? "bg-slate-800 text-white font-semibold" : "text-slate-600 hover:bg-slate-200/60 hover:text-slate-900"}`;

const MORE_WIDTH = 76;

function Dropdown({ label, active, items, activeLeaf, testId }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);
    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen((v) => !v)} className={tabClass(active)} aria-expanded={open} data-testid={testId}>
                {label} ▾
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1 min-w-[220px] bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-40" role="menu">
                    {items.map((leaf) => (
                        <NavLink
                            key={leaf.to}
                            to={leaf.to}
                            role="menuitem"
                            onClick={() => setOpen(false)}
                            className={() => `block px-3 py-1.5 text-sm ${leaf.to === activeLeaf ? "font-semibold text-yellow-800 bg-yellow-50" : "text-slate-700 hover:bg-slate-50"}`}
                        >
                            {leaf.prefix ? <span className="text-slate-400">{leaf.prefix} · </span> : null}
                            {leaf.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

function leavesOf(item, prefix) {
    if (!isNavGroup(item)) return [{ ...item, prefix }];
    return item.children.flatMap((c) => leavesOf(c, isNavGroup(c) ? c.label : prefix));
}

export default function TopSubmenu({ items, layout, pathname }) {
    const trail = layout === "top" ? activeTrail(items, pathname) : [];
    const group = trail.length > 1 ? items.find((i) => navItemKey(i) === trail[0]) : null;
    const children = group ? group.children : [];
    const activeLeaf = trail[trail.length - 1];
    const boxRef = useRef(null);
    const measureRef = useRef(null);
    const [fit, setFit] = useState(null); // сколько пунктов помещается; null — все
    const sig = children.map(navItemKey).join("|");

    const measure = useCallback(() => {
        const box = boxRef.current;
        const row = measureRef.current;
        if (!box || !row) return;
        const avail = box.clientWidth;
        const widths = Array.from(row.children).map((el) => el.offsetWidth + 4);
        if (!avail || !widths.length || widths.every((w) => w <= 4)) {
            setFit(null);
            return;
        }
        const total = widths.reduce((a, b) => a + b, 0);
        if (total <= avail) {
            setFit(null);
            return;
        }
        let used = MORE_WIDTH;
        let n = 0;
        while (n < widths.length && used + widths[n] <= avail) {
            used += widths[n];
            n += 1;
        }
        setFit(Math.max(1, n));
    }, []);

    useLayoutEffect(() => { measure(); }, [measure, sig]);
    useEffect(() => {
        if (!group) return undefined;
        if (typeof ResizeObserver !== "undefined" && boxRef.current) {
            const ro = new ResizeObserver(() => measure());
            ro.observe(boxRef.current);
            return () => ro.disconnect();
        }
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [group, measure]);

    if (!group) return <div className="flex-1 min-w-0" />;

    const renderTab = (child) => {
        const key = navItemKey(child);
        if (isNavGroup(child)) {
            return (
                <Dropdown
                    key={key}
                    label={child.label}
                    active={trail.includes(key)}
                    items={leavesOf(child, null)}
                    activeLeaf={activeLeaf}
                    testId={`top-submenu-group-${child.label}`}
                />
            );
        }
        return (
            <NavLink key={key} to={child.to} className={() => tabClass(activeLeaf === child.to)} data-testid="top-submenu-tab">
                {child.label}
            </NavLink>
        );
    };

    const shown = fit === null ? children : children.slice(0, fit);
    const rest = fit === null ? [] : children.slice(fit);
    const restLeaves = rest.flatMap((c) => leavesOf(c, isNavGroup(c) ? c.label : null));
    const restActive = rest.some((c) => leafPaths(c).includes(activeLeaf));

    return (
        <nav className="flex-1 min-w-0 flex items-center gap-2" aria-label={`Разделы: ${group.label}`} data-testid="top-submenu">
            <span className="hidden xl:inline text-xs font-bold uppercase tracking-wide text-slate-400 whitespace-nowrap">{group.label}</span>
            <div ref={boxRef} className="relative flex-1 min-w-0">
                <div className="flex items-center gap-1">
                    {shown.map(renderTab)}
                    {rest.length > 0 && (
                        <Dropdown label="Ещё" active={restActive} items={restLeaves} activeLeaf={activeLeaf} testId="top-submenu-more" />
                    )}
                </div>
                <div ref={measureRef} aria-hidden="true" className="absolute left-0 top-0 flex items-center gap-1 invisible pointer-events-none h-0 overflow-hidden">
                    {children.map((child) => (
                        <span key={navItemKey(child)} className={tabClass(false)}>
                            {child.label}{isNavGroup(child) ? " ▾" : ""}
                        </span>
                    ))}
                </div>
            </div>
        </nav>
    );
}
